import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { useTaskTypes } from '../../contexts/TaskTypesContext';
import './Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewTaskType = forwardRef(({ taskTypeId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onTaskTypeCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [taskType, setTaskType] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError } = useMessages();
  const { refreshTaskTypes } = useTaskTypes();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (taskTypeId && !createMode) {
      fetchTaskType();
    } else if (createMode) {
      const newTaskType = {
        task_type_code: '',
        task_type_name: '',
        description: '',
        category: 'WORKFLOW',
        icon: '',
        color_hex: '#6c757d',
        config_table_name: '',
        special_form: '',
        is_active: true
      };
      setTaskType(newTaskType);
      setEditData(newTaskType);
    }
  }, [taskTypeId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editMode, createMode, onBack]);

  const fetchTaskType = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/task-types/${taskTypeId}`));
      if (response.ok) {
        const data = await response.json();
        setTaskType(data);
        setEditData(data);
      } else {
        setError(t('taskTypes.view.error.fetchFailed'));
      }
    } catch (err) {
      console.error('Error fetching task type:', err);
      setError(t('taskTypes.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      if (!editData.task_type_code || !editData.task_type_name) {
        showError(t('taskTypes.view.validation.required'));
        return;
      }

      const saveData = {
        task_type_code: editData.task_type_code,
        task_type_name: editData.task_type_name,
        description: editData.description || null,
        category: editData.category,
        icon: editData.icon || null,
        color_hex: editData.color_hex || null,
        config_table_name: editData.config_table_name || null,
        special_form: editData.special_form || null,
        is_active: editData.is_active
      };

      const url = createMode
        ? getApiUrl('/api/task-types/')
        : getApiUrl(`/api/task-types/${taskTypeId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedTaskType = await response.json();
        setTaskType(updatedTaskType);
        setEditData(updatedTaskType);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('taskTypes.view.messages.createdSuccess'));
          if (onTaskTypeCreated) {
            onTaskTypeCreated(updatedTaskType.task_type_id);
          }
        } else {
          showSuccess(t('taskTypes.view.messages.updatedSuccess'));
        }

        // Refresh the global context
        refreshTaskTypes();

        if (onSave) onSave();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (err) {
      console.error('Error saving task type:', err);
      showError(tCommon('errors.genericTitle'), t('taskTypes.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('taskTypes.view.confirmations.unsavedChanges'))) {
        setEditData(taskType || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('taskTypes.view.confirmations.deleteConfirm', { name: taskType.task_type_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/task-types/${taskTypeId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('taskTypes.view.messages.deletedSuccess'));
        refreshTaskTypes();
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (err) {
      console.error('Error deleting task type:', err);
      showError(tCommon('errors.genericTitle'), t('taskTypes.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('taskTypes.loading')}</div></div>;
  }

  if (error && !taskType) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!taskType && !createMode) {
    return <div className="view-container"><div className="no-data">{t('taskTypes.view.titles.noTaskTypeFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('taskTypes.view.titles.newTaskType') : taskType?.task_type_name || t('taskTypes.view.titles.viewTaskType')}
          </h2>
          <span className="header-id">{!createMode && taskType?.task_type_code}</span>
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('taskTypes.filters.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('taskTypes.filters.status.inactive')}</span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('taskTypes.view.sections.taskTypeInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('taskTypes.view.fields.code')} *</label>
              <input
                type="text"
                value={editData.task_type_code || ''}
                onChange={(e) => handleFieldChange('task_type_code', e.target.value.toUpperCase())}
                disabled={!editMode && !createMode}
                required
                maxLength="50"
              />
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.name')} *</label>
              <input
                type="text"
                value={editData.task_type_name || ''}
                onChange={(e) => handleFieldChange('task_type_name', e.target.value)}
                disabled={!editMode && !createMode}
                required
              />
            </div>
            <div className="form-group full-width">
              <label>{t('taskTypes.view.fields.description')}</label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.category')}</label>
              <select
                value={editData.category || 'WORKFLOW'}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                disabled={!editMode && !createMode}
              >
                <option value="WORKFLOW">{t('taskTypes.filters.categories.workflow')}</option>
                <option value="PRODUCTION">{t('taskTypes.filters.categories.production')}</option>
                <option value="MAINTENANCE">{t('taskTypes.filters.categories.maintenance')}</option>
                <option value="OTHER">{t('taskTypes.filters.categories.other')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.icon')}</label>
              <input
                type="text"
                value={editData.icon || ''}
                onChange={(e) => handleFieldChange('icon', e.target.value)}
                disabled={!editMode && !createMode}
                maxLength="10"
                placeholder={t('taskTypes.view.placeholders.icon')}
              />
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.colorHex')}</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={editData.color_hex || '#6c757d'}
                  onChange={(e) => handleFieldChange('color_hex', e.target.value)}
                  disabled={!editMode && !createMode}
                  style={{ width: '40px', height: '32px', padding: '2px', cursor: editMode || createMode ? 'pointer' : 'default' }}
                />
                <input
                  type="text"
                  value={editData.color_hex || ''}
                  onChange={(e) => handleFieldChange('color_hex', e.target.value)}
                  disabled={!editMode && !createMode}
                  maxLength="7"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.configTable')}</label>
              <input
                type="text"
                value={editData.config_table_name || ''}
                onChange={(e) => handleFieldChange('config_table_name', e.target.value)}
                disabled={!editMode && !createMode}
                placeholder={t('taskTypes.view.placeholders.configTable')}
              />
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.specialForm')}</label>
              <select
                value={editData.special_form || ''}
                onChange={(e) => handleFieldChange('special_form', e.target.value)}
                disabled={!editMode && !createMode}
              >
                <option value="">{t('taskTypes.view.specialForms.none')}</option>
                <option value="PRINTING">{t('taskTypes.view.specialForms.printing')}</option>
                <option value="DIE_CUTTING">{t('taskTypes.view.specialForms.dieCutting')}</option>
                <option value="SEAMED">{t('taskTypes.view.specialForms.seamed')}</option>
                <option value="SETUP">{t('taskTypes.view.specialForms.setup')}</option>
                <option value="QUALITY_CHECK">{t('taskTypes.view.specialForms.qualityCheck')}</option>
                <option value="MATERIAL_PREP">{t('taskTypes.view.specialForms.materialPrep')}</option>
              </select>
              {!editMode && !createMode && editData.special_form && (
                <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>
                  {t('taskTypes.view.fields.specialFormHint')}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>{t('taskTypes.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={!editMode && !createMode}
              >
                <option value="active">{t('taskTypes.filters.status.active')}</option>
                <option value="inactive">{t('taskTypes.filters.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Preview */}
        {editData.task_type_code && (
          <div className="form-section">
            <h3>{t('taskTypes.view.sections.preview')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px' }}>
              <span
                style={{
                  backgroundColor: editData.color_hex || '#6c757d',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {editData.icon && <span>{editData.icon}</span>}
                {editData.task_type_name || editData.task_type_code}
              </span>
            </div>
          </div>
        )}

        {taskType && taskType.created_date && (
          <div className="form-section">
            <h3>{t('taskTypes.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('taskTypes.view.fields.createdDate')}</label>
                <span>{formatAppDate(taskType.created_date, 'datetime')}</span>
              </div>
              {taskType.updated_date && (
                <div className="info-item">
                  <label>{t('taskTypes.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(taskType.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewTaskType;
